/**
 * Track Zone Analysis
 *
 * Main orchestration module for analyzing track zones.
 * Coordinates zone detection, metrics calculation, and impact assessment
 * to produce actionable insights for riders.
 */

import { type Lap } from '../../domain/lap/Lap'
import {
  type TrackZone,
  type ZoneAnalysisResult,
  type ZoneDetectionConfig,
  DEFAULT_ZONE_DETECTION_CONFIG,
} from './TrackZoneTypes'
import {
  extractZoneData,
  detectZoneBoundaries,
  alignLapDataByDistance,
  type ZoneDataPoint,
} from './ZoneDetection'
import { calculateConsensusMetrics } from './ZoneConsensus'
import { calculateVarianceMetrics } from './ZoneVariance'
import { calculateZoneLapData } from './ZoneLapData'
import { calculateZoneImpact } from './ZoneImpact'
import { average, smoothedAcceleration } from './mathUtils'

// ============================================================================
// Main Analysis Function
// ============================================================================

/**
 * Analyze track zones from a collection of laps.
 *
 * Detects distinct zones (braking, cornering, acceleration, straight) and
 * calculates consistency metrics to identify areas for improvement.
 *
 * @param laps - Array of laps to analyze (minimum 4 recommended)
 * @param config - Zone detection configuration
 * @returns Complete zone analysis with metrics and recommendations
 */
export function analyzeTrackZones(
  laps: Lap[],
  config: ZoneDetectionConfig = DEFAULT_ZONE_DETECTION_CONFIG,
  detectionLaps?: Lap[]
): ZoneAnalysisResult {
  // Extract telemetry data from metrics laps and pre-smooth acceleration
  // at the full-lap level. GPS-derived acceleration amplifies speed noise —
  // a 9-point median on the full lap (~400 points) works well. Per-zone
  // smoothing fails when zones are small (4-5 points) because the median
  // window has no surrounding context.
  const metricsLapData = laps
    .map(extractZoneData)
    .filter((d) => d.length > 0)
    .map(smoothLapAcceleration)
  const lapIds = laps.map((l) => l.id)
  const lapNumbers = laps.map((l) => l.lapNumber)
  const sessionIds = laps.map((l) => l.sessionId)

  if (metricsLapData.length < 2) {
    return createEmptyResult()
  }

  // Use separate detection laps for stable zone boundaries when provided.
  // Zone boundaries come from ALL laps (consistent regardless of filtering),
  // while metrics are calculated from the filtered subset only.
  const usesSeparateDetection =
    detectionLaps != null &&
    detectionLaps !== laps &&
    detectionLaps.length >= config.minSamplesPerZone

  let detectionLapData: ZoneDataPoint[][]
  if (usesSeparateDetection) {
    detectionLapData = detectionLaps!
      .map(extractZoneData)
      .filter((d) => d.length > 0)
      .map(smoothLapAcceleration)
    if (detectionLapData.length < config.minSamplesPerZone) {
      detectionLapData = metricsLapData
    }
  } else {
    detectionLapData = metricsLapData
  }

  if (detectionLapData.length < config.minSamplesPerZone) {
    return createEmptyResult()
  }

  // Detect zone boundaries using detection laps
  const boundaries = detectZoneBoundaries(detectionLapData, config)

  // When using separate detection laps, re-extract per-zone data from metrics laps
  // so that consistency scores and lap data reflect only the analyzed laps
  let metricsPerZone: ZoneDataPoint[][][]
  if (usesSeparateDetection && detectionLapData !== metricsLapData) {
    const alignedMetrics = alignLapDataByDistance(metricsLapData)
    if (alignedMetrics.length === 0 || alignedMetrics[0].length < 2) {
      return createEmptyResult()
    }
    metricsPerZone = boundaries.map((boundary) =>
      alignedMetrics.map((lapData) =>
        lapData.filter(
          (p) =>
            p.distance >= boundary.startDistance &&
            p.distance <= boundary.endDistance
        )
      )
    )
  } else {
    metricsPerZone = boundaries.map((b) => b.dataPoints)
  }

  // Track count per zone type for naming (e.g., "Braking Zone 1", "Braking Zone 2")
  const typeCount: Record<string, number> = {}

  // Calculate metrics for each zone using metrics data
  const zones = boundaries.map((boundary, idx) => {
    const zoneData = metricsPerZone[idx]
    const consensus = calculateConsensusMetrics(zoneData)
    const variance = calculateVarianceMetrics(
      zoneData,
      config,
      boundary.type,
      boundary.startDistance
    )
    const zoneLength = boundary.endDistance - boundary.startDistance
    const impact = calculateZoneImpact(
      boundary.type,
      variance,
      consensus,
      zoneLength,
      zoneData
    )

    // Center point from detection data (stable position for map)
    const centerIdx = Math.floor(boundary.dataPoints[0].length / 2)
    const centerPoint = boundary.dataPoints[0][centerIdx]

    // Increment type-specific counter for naming
    typeCount[boundary.type] = (typeCount[boundary.type] ?? 0) + 1
    const typeIndex = typeCount[boundary.type]

    return createTrackZone(
      idx,
      typeIndex,
      boundary,
      consensus,
      variance,
      impact,
      centerPoint
    )
  })

  // Rank zones by impact
  rankZonesByImpact(zones)

  // Identify focus zones: top 5 most impactful actionable zones
  // Exclude straight zones (not actionable) but include braking, cornering, acceleration
  // Require poor consistency AND meaningful time impact
  const focusZones = identifyFocusZones(zones)

  // Calculate per-lap data for each zone
  const zoneLapData = new Map<string, ReturnType<typeof calculateZoneLapData>>()
  boundaries.forEach((boundary, idx) => {
    const zone = zones[idx]
    const zoneData = metricsPerZone[idx]
    const lapData = calculateZoneLapData(
      zone.id,
      zoneData,
      lapIds,
      lapNumbers,
      zone.consensus,
      boundary.startDistance,
      sessionIds
    )
    zoneLapData.set(zone.id, lapData)
  })

  // Calculate track-level statistics
  const trackStats = calculateTrackStats(zones, metricsLapData)

  return {
    zones,
    focusZones,
    zoneLapData,
    trackStats,
  }
}

// ============================================================================
// Focus Zone Identification
// ============================================================================

/**
 * Identify focus zones using adaptive thresholds.
 *
 * The base threshold (300ms time loss + <90 consistency) works well when
 * variance is concentrated in a few zones. But for consistent riders,
 * time loss is distributed across many zones — each individually small
 * but adding up to significant lap time variation.
 *
 * When cumulative time loss is significant (≥500ms) but no zone meets
 * the base threshold, we progressively lower the time loss threshold
 * to surface the top contributors to that cumulative loss.
 */
function identifyFocusZones(zones: TrackZone[]): TrackZone[] {
  const BASE_TIME_LOSS_THRESHOLD = 200
  const BASE_CONSISTENCY_THRESHOLD = 80

  // Actionable zones only (exclude straights)
  const actionableZones = zones.filter(
    (z) => z.type !== 'straight' && z.type !== 'acceleration'
  )

  // Try with base thresholds first
  const baseResult = actionableZones
    .filter(
      (z) =>
        z.variance.consistencyScore < BASE_CONSISTENCY_THRESHOLD &&
        z.impact.estimatedTimeLossMs >= BASE_TIME_LOSS_THRESHOLD
    )
    .sort((a, b) => b.impact.estimatedTimeLossMs - a.impact.estimatedTimeLossMs)
    .slice(0, 5)

  if (baseResult.length > 0) {
    return baseResult.sort((a, b) => a.startDistance - b.startDistance)
  }

  // No zones met base thresholds. Check if there's significant cumulative loss
  // that's distributed across many zones (the "consistent but slow" pattern).
  const totalTimeLoss = actionableZones.reduce(
    (sum, z) => sum + z.impact.estimatedTimeLossMs,
    0
  )

  // If total cumulative loss is significant (≥500ms), lower the time threshold
  // to show where the distributed time is being lost.
  // Keep the consistency threshold the same — zones above 85% are performing
  // well and shouldn't appear as focus areas even in adaptive mode.
  if (totalTimeLoss >= 500) {
    const adaptiveResult = actionableZones
      .filter(
        (z) =>
          z.variance.consistencyScore < BASE_CONSISTENCY_THRESHOLD &&
          z.impact.estimatedTimeLossMs >= 100
      )
      .sort(
        (a, b) => b.impact.estimatedTimeLossMs - a.impact.estimatedTimeLossMs
      )
      .slice(0, 5)

    if (adaptiveResult.length > 0) {
      return adaptiveResult.sort((a, b) => a.startDistance - b.startDistance)
    }
  }

  return []
}

// ============================================================================
// Helper Functions
// ============================================================================

function createEmptyResult(): ZoneAnalysisResult {
  return {
    zones: [],
    focusZones: [],
    zoneLapData: new Map(),
    trackStats: {
      totalZones: 0,
      highVarianceZones: 0,
      overallConsistency: 0,
      totalTimeLossMs: 0,
      trackLength: 0,
    },
  }
}

function createTrackZone(
  idx: number,
  typeIndex: number,
  boundary: ReturnType<typeof detectZoneBoundaries>[0],
  consensus: ReturnType<typeof calculateConsensusMetrics>,
  variance: ReturnType<typeof calculateVarianceMetrics>,
  impact: ReturnType<typeof calculateZoneImpact>,
  centerPoint?: {
    lat: number
    lng: number
    distance: number
    speed: number
    acceleration: number
    leanAngle: number
  }
): TrackZone {
  const typeName =
    boundary.type.charAt(0).toUpperCase() + boundary.type.slice(1)

  return {
    id: `zone-${idx + 1}`,
    type: boundary.type,
    startDistance: boundary.startDistance,
    endDistance: boundary.endDistance,
    name: `${typeName} Zone ${typeIndex}`,
    consensus,
    variance,
    impact,
    centerPoint: centerPoint
      ? { lat: centerPoint.lat, lng: centerPoint.lng }
      : undefined,
  }
}

function rankZonesByImpact(zones: TrackZone[]): void {
  const sorted = [...zones].sort(
    (a, b) => b.impact.estimatedTimeLossMs - a.impact.estimatedTimeLossMs
  )
  sorted.forEach((zone, idx) => {
    zone.impact.rank = idx + 1
  })
}

function calculateTrackStats(
  zones: TrackZone[],
  allLapData: ReturnType<typeof extractZoneData>[]
) {
  const trackLength =
    allLapData[0]?.length > 0
      ? allLapData[0][allLapData[0].length - 1].distance
      : 0

  const highVarianceZones = zones.filter(
    (z) => z.variance.consistencyScore < 70
  ).length
  const overallConsistency =
    zones.length > 0
      ? Math.round(average(zones.map((z) => z.variance.consistencyScore)))
      : 100
  const totalTimeLossMs = zones.reduce(
    (sum, z) => sum + z.impact.estimatedTimeLossMs,
    0
  )

  return {
    totalZones: zones.length,
    highVarianceZones,
    overallConsistency,
    totalTimeLossMs,
    trackLength,
  }
}

/**
 * Returns a copy of the lap data with acceleration smoothed via 9-point median
 * over the full lap (~400 points). Must happen before zone slicing so even tiny
 * zones (4-5 pts) benefit from surrounding lap context.
 */
function smoothLapAcceleration(lapData: ZoneDataPoint[]): ZoneDataPoint[] {
  return lapData.map((pt, i) => ({
    ...pt,
    acceleration: smoothedAcceleration(lapData, i),
  }))
}
